/**
 * useNotification Hook
 * Phase 1 Day 5 - Notification management with deduplication
 *
 * Manages notification display with built-in deduplication to prevent
 * rapid duplicate notifications from overwhelming the user.
 *
 * @returns {Object} Notification state and management functions
 */

import { useCallback, useRef, useEffect } from "react";
import { useUIStore } from "../stores";
import logger from "../utils/logger";

// Deduplication window in milliseconds
const DEDUP_WINDOW_MS = 100;

export const useNotification = () => {
  // Get notification state from UIStore
  const notification = useUIStore((state) => state.notification);
  const showNotificationStore = useUIStore((state) => state.showNotification);
  const clearNotification = useUIStore((state) => state.clearNotification);

  // Ref to track last notification for deduplication
  const lastNotificationRef = useRef(null);

  /**
   * Show a notification with automatic deduplication
   * Prevents showing the same notification within the deduplication window
   * @param {Object|string} notificationData - Notification data or message string
   */
  const showNotification = useCallback(
    (notificationData) => {
      // Normalize input to object format
      const data =
        typeof notificationData === "string"
          ? { message: notificationData, type: "info" }
          : notificationData;

      logger.log(
        `[useNotification] Show notification: "${data.message}" (${
          data.type || "info"
        })`
      );

      // Check for duplicate within window
      const now = Date.now();
      const last = lastNotificationRef.current;

      if (
        last &&
        now - last.time < DEDUP_WINDOW_MS &&
        last.message === data.message
      ) {
        logger.log("[useNotification] Duplicate notification ignored");
        return;
      }

      // Update last notification tracker
      lastNotificationRef.current = {
        time: now,
        message: data.message,
      };

      // Show notification via store
      showNotificationStore(data.message, data.type);
    },
    [showNotificationStore]
  );

  /**
   * Show success notification
   * @param {string} message - Success message
   */
  const showSuccess = useCallback(
    (message) => {
      showNotification({ message, type: "success" });
    },
    [showNotification]
  );

  /**
   * Show error notification
   * @param {string} message - Error message
   */
  const showError = useCallback(
    (message) => {
      showNotification({ message, type: "error" });
    },
    [showNotification]
  );

  /**
   * Show info notification
   * @param {string} message - Info message
   */
  const showInfo = useCallback(
    (message) => {
      showNotification({ message, type: "info" });
    },
    [showNotification]
  );

  /**
   * Show warning notification
   * @param {string} message - Warning message
   */
  const showWarning = useCallback(
    (message) => {
      showNotification({ message, type: "warning" });
    },
    [showNotification]
  );

  /**
   * Clear the current notification
   */
  const clear = useCallback(() => {
    logger.log("[useNotification] Clearing notification");
    clearNotification();
  }, [clearNotification]);

  /**
   * Check if a notification is currently displayed
   * @returns {boolean} True if notification is active
   */
  const hasNotification = () => {
    return notification !== null;
  };

  // Auto-clear notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => clearNotification(), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification, clearNotification]);

  return {
    // State
    notification,
    lastNotificationRef,

    // Core actions
    showNotification,
    clearNotification, // Backward compatibility
    clear,

    // Type-specific helpers
    showSuccess,
    showError,
    showInfo,
    showWarning,

    // Utilities
    hasNotification,
  };
};
